package com.example.hukakmp

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.example.hukakmp.auth.GoogleAuthProvider
import com.example.hukakmp.auth.googleSignInAvailable
import com.example.hukakmp.data.AchievementsRepository
import com.example.hukakmp.data.AiQuotaRepository
import com.example.hukakmp.data.AuthRepository
import com.example.hukakmp.data.AuthState
import com.example.hukakmp.data.FishingRepository
import com.example.hukakmp.data.PescadexRepository
import com.example.hukakmp.data.TournamentRepository
import com.example.hukakmp.reportchat.toNuevoParteInput
import com.example.hukakmp.ui.AchievementsScreen
import com.example.hukakmp.ui.ConversationalReportScreen
import com.example.hukakmp.ui.FishIdentificationScreen
import com.example.hukakmp.ui.FishCounterScreen
import com.example.hukakmp.ui.HomeScreen
import com.example.hukakmp.ui.HukaChatScreen
import com.example.hukakmp.ui.LoginScreen
import com.example.hukakmp.ui.ParteWizardScreen
import com.example.hukakmp.ui.PescadexScreen
import com.example.hukakmp.ui.ReportesScreen
import com.example.hukakmp.ui.TournamentsScreen
import com.example.hukakmp.ui.theme.HukaTheme
import com.example.hukakmp.viewmodel.AchievementsViewModel
import com.example.hukakmp.viewmodel.ConversationalReportViewModel
import com.example.hukakmp.viewmodel.FishIdentificationViewModel
import com.example.hukakmp.viewmodel.FishCounterViewModel
import com.example.hukakmp.viewmodel.HukaChatViewModel
import com.example.hukakmp.viewmodel.LoginViewModel
import com.example.hukakmp.viewmodel.ParteWizardViewModel
import com.example.hukakmp.viewmodel.PescadexViewModel
import com.example.hukakmp.viewmodel.ReportesViewModel
import com.example.hukakmp.viewmodel.TournamentViewModel

private enum class Pantalla { INICIO, CHAT, PARTE_CHAT, CONTADOR, TORNEOS, REPORTES, NUEVO_PARTE, PESCADEX, IDENTIFICAR, LOGROS }

@Composable
fun App(googleAuthProvider: GoogleAuthProvider) {
    HukaTheme {
        val authRepository = remember { AuthRepository() }
        val loginViewModel = remember { LoginViewModel(authRepository, googleAuthProvider) }
        val authState by loginViewModel.authState.collectAsState()
        val loginUiState by loginViewModel.loginUiState.collectAsState()

        when (val state = authState) {
            is AuthState.Authenticated -> {
                val fishingRepository = remember { FishingRepository() }
                val achievementsRepository = remember { AchievementsRepository() }
                val pescadexRepository = remember { PescadexRepository() }
                val aiQuotaRepository = remember { AiQuotaRepository() }
                val tournamentRepository = remember { TournamentRepository() }
                val reportesViewModel = remember { ReportesViewModel(fishingRepository) }
                val wizardViewModel = remember(state.user.uid) {
                    ParteWizardViewModel(fishingRepository, state.user.uid)
                }
                val pescadexViewModel = remember { PescadexViewModel(pescadexRepository) }
                val identificationViewModel = remember {
                    FishIdentificationViewModel(aiQuotaRepository)
                }
                val chatViewModel = remember(state.user.uid) {
                    HukaChatViewModel(state.user.uid, aiQuotaRepository)
                }
                val conversationalReportViewModel = remember {
                    ConversationalReportViewModel(aiQuotaRepository)
                }
                val counterViewModel = remember { FishCounterViewModel() }
                val tournamentViewModel = remember { TournamentViewModel(tournamentRepository) }
                val achievementsViewModel = remember {
                    AchievementsViewModel(achievementsRepository, fishingRepository)
                }
                val uiState by reportesViewModel.uiState.collectAsState()
                val wizardState by wizardViewModel.uiState.collectAsState()
                val pescadexState by pescadexViewModel.uiState.collectAsState()
                val identificationState by identificationViewModel.uiState.collectAsState()
                val chatState by chatViewModel.uiState.collectAsState()
                val conversationalReportState by conversationalReportViewModel.uiState.collectAsState()
                val counterState by counterViewModel.uiState.collectAsState()
                val tournamentState by tournamentViewModel.uiState.collectAsState()
                val achievementsState by achievementsViewModel.uiState.collectAsState()

                var pantalla by remember { mutableStateOf(Pantalla.INICIO) }

                LaunchedEffect(wizardState.guardadoExitoso) {
                    if (wizardState.guardadoExitoso) {
                        reportesViewModel.cargarReportes()
                        achievementsViewModel.refresh()
                        pantalla = Pantalla.REPORTES
                    }
                }

                when (pantalla) {
                    Pantalla.INICIO -> HomeScreen(
                        user = state.user,
                        reportesState = uiState,
                        onNuevoParte = {
                            wizardViewModel.prepararNuevoParte()
                            pantalla = Pantalla.NUEVO_PARTE
                        },
                        onReportes = { pantalla = Pantalla.REPORTES },
                        onPescadex = {
                            pescadexViewModel.refresh()
                            pantalla = Pantalla.PESCADEX
                        },
                        onIdentificar = {
                            identificationViewModel.clear()
                            pantalla = Pantalla.IDENTIFICAR
                        },
                        onChat = { pantalla = Pantalla.CHAT },
                        onContador = { pantalla = Pantalla.CONTADOR },
                        onTorneos = {
                            tournamentViewModel.refresh()
                            pantalla = Pantalla.TORNEOS
                        },
                        onLogros = { pantalla = Pantalla.LOGROS },
                        onSignOut = loginViewModel::signOut,
                    )

                    Pantalla.CHAT -> HukaChatScreen(
                        uiState = chatState,
                        onSend = chatViewModel::send,
                        onClearError = chatViewModel::clearError,
                        onCreateReport = {
                            wizardViewModel.prepararNuevoParte()
                            pantalla = Pantalla.NUEVO_PARTE
                        },
                        onCreateReportByChat = {
                            conversationalReportViewModel.clearDraft()
                            conversationalReportViewModel.refreshQuota()
                            pantalla = Pantalla.PARTE_CHAT
                        },
                        onBack = { pantalla = Pantalla.INICIO },
                    )

                    Pantalla.PARTE_CHAT -> ConversationalReportScreen(
                        uiState = conversationalReportState,
                        onAnalyze = conversationalReportViewModel::analyze,
                        onClearDraft = conversationalReportViewModel::clearDraft,
                        onClearError = conversationalReportViewModel::clearError,
                        onContinue = { draft ->
                            wizardViewModel.iniciarDesdeConversacion(draft.toNuevoParteInput())
                            pantalla = Pantalla.NUEVO_PARTE
                        },
                        onBack = { pantalla = Pantalla.CHAT },
                    )

                    Pantalla.CONTADOR -> FishCounterScreen(
                        uiState = counterState,
                        onAdd = counterViewModel::add,
                        onIncrement = counterViewModel::increment,
                        onDecrement = counterViewModel::decrement,
                        onReturned = counterViewModel::setReturned,
                        onRemove = counterViewModel::remove,
                        onClear = counterViewModel::clear,
                        onCreateReport = {
                            wizardViewModel.iniciarDesdeContador(counterState.entries)
                            pantalla = Pantalla.NUEVO_PARTE
                        },
                        onBack = { pantalla = Pantalla.INICIO },
                    )

                    Pantalla.TORNEOS -> TournamentsScreen(
                        uiState = tournamentState,
                        onRefresh = tournamentViewModel::refresh,
                        onCreate = tournamentViewModel::create,
                        onSearch = tournamentViewModel::search,
                        onJoin = tournamentViewModel::requestJoin,
                        onRespond = tournamentViewModel::respond,
                        onRejectReport = tournamentViewModel::rejectReport,
                        onDismissCreatedCode = tournamentViewModel::dismissCreatedCode,
                        onDismissFound = tournamentViewModel::dismissFoundTournament,
                        onClearRequestSent = tournamentViewModel::clearRequestSent,
                        onClearError = tournamentViewModel::clearError,
                        onBack = { pantalla = Pantalla.INICIO },
                    )

                    Pantalla.REPORTES -> ReportesScreen(
                        uiState = uiState,
                        puedeEliminarse = reportesViewModel::puedeEliminarse,
                        onEliminar = reportesViewModel::eliminarParte,
                        onBack = { pantalla = Pantalla.INICIO },
                        onNuevoParte = {
                            wizardViewModel.prepararNuevoParte()
                            pantalla = Pantalla.NUEVO_PARTE
                        },
                    )

                    Pantalla.NUEVO_PARTE -> ParteWizardScreen(
                        uiState = wizardState,
                        onActualizar = wizardViewModel::actualizar,
                        onAgregarEspecie = wizardViewModel::agregarEspecie,
                        onActualizarEspecie = wizardViewModel::actualizarEspecie,
                        onQuitarEspecie = wizardViewModel::quitarEspecie,
                        onSiguiente = wizardViewModel::siguiente,
                        onAnterior = wizardViewModel::anterior,
                        onGuardar = wizardViewModel::guardar,
                        onDescartarBorrador = wizardViewModel::descartarBorrador,
                        onCancelar = { pantalla = Pantalla.INICIO },
                    )

                    Pantalla.PESCADEX -> PescadexScreen(
                        uiState = pescadexState,
                        onBack = { pantalla = Pantalla.INICIO },
                        onRefresh = pescadexViewModel::refresh,
                    )

                    Pantalla.IDENTIFICAR -> FishIdentificationScreen(
                        uiState = identificationState,
                        onAnalyze = identificationViewModel::analyze,
                        onClear = identificationViewModel::clear,
                        onRetry = identificationViewModel::retry,
                        onCreateReport = { foto, especies ->
                            wizardViewModel.iniciarDesdeIdentificacion(
                                foto = foto,
                                especies = especies.map { it.commonName },
                            )
                            pantalla = Pantalla.NUEVO_PARTE
                        },
                        onBack = { pantalla = Pantalla.INICIO },
                    )

                    Pantalla.LOGROS -> AchievementsScreen(
                        uiState = achievementsState,
                        onBack = { pantalla = Pantalla.INICIO },
                        onRefresh = achievementsViewModel::refresh,
                    )
                }
            }

            else -> {
                LoginScreen(
                    authState = state,
                    loginUiState = loginUiState,
                    showGoogleSignIn = googleSignInAvailable,
                    onGoogleSignIn = loginViewModel::signInWithGoogle,
                )
            }
        }
    }
}
